import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { updateSkill, generateSection } from '../api'

interface Section {
  title: string
  content: string
}

const SECTION_HINTS: Record<string, { subtitle: string; aiExplanation: string; contextPrompt: string }> = {
  Overview: {
    subtitle: 'The intro paragraph that sets the tone for the entire skill.',
    aiExplanation: 'This section tells the AI who it is and what it does. A strong overview leads to better, more consistent results across all interactions.',
    contextPrompt: 'What does your team do? What tone should this skill use?',
  },
  Guidelines: {
    subtitle: 'Rules and constraints the AI should follow.',
    aiExplanation: 'Guidelines keep the AI focused. Without clear guardrails, outputs drift. Add your team\'s specific preferences here — things like tone, length, formats you use.',
    contextPrompt: 'What rules or preferences should the AI follow for your team?',
  },
  Input: {
    subtitle: 'What the user provides to this skill.',
    aiExplanation: 'This tells the AI what to expect from users. Clear input definitions mean the AI asks the right follow-up questions and doesn\'t make assumptions.',
    contextPrompt: 'What will your team typically provide when using this skill?',
  },
  Output: {
    subtitle: 'What the skill produces.',
    aiExplanation: 'Define what "done" looks like. The more specific your output format, the more consistent and usable the results will be.',
    contextPrompt: 'What format should the output be in? Any specific structure your team expects?',
  },
  Process: {
    subtitle: 'Step-by-step workflow the AI follows.',
    aiExplanation: 'A clear process ensures the AI works methodically instead of jumping to conclusions. Each step builds on the previous one.',
    contextPrompt: 'Walk through how this process works at your company.',
  },
  default: {
    subtitle: 'Customize this section for your team.',
    aiExplanation: 'This section contains specific instructions for the AI. Tailor it to match how your team works and what you need.',
    contextPrompt: 'Tell us about your team and how you\'d use this section.',
  },
}

function getHint(title: string) {
  if (SECTION_HINTS[title]) return SECTION_HINTS[title]
  const lower = title.toLowerCase()
  if (lower.includes('guideline') || lower.includes('rule')) return SECTION_HINTS.Guidelines
  if (lower.includes('input') || lower.includes('ask')) return SECTION_HINTS.Input
  if (lower.includes('output') || lower.includes('format') || lower.includes('result')) return SECTION_HINTS.Output
  if (lower.includes('process') || lower.includes('step') || lower.includes('workflow')) return SECTION_HINTS.Process
  return SECTION_HINTS.default
}

export default function TemplateBuilderPage() {
  const { name } = useParams<{ name: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { session } = useAuth()

  const state = location.state as { skillName?: string; sections?: Section[]; templateTitle?: string } | null
  const skillName = state?.skillName || name || ''
  const templateTitle = state?.templateTitle || name || ''

  const [sections, setSections] = useState<Section[]>(state?.sections || [])
  const [current, setCurrent] = useState(0)
  const [completed, setCompleted] = useState<Set<number>>(new Set())
  const [userContext, setUserContext] = useState('')
  const [generating, setGenerating] = useState(false)
  const [suggestion, setSuggestion] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState('')

  useEffect(() => {
    if (!state?.sections || !state?.skillName) {
      navigate('/templates', { replace: true })
    }
  }, [state, navigate])

  if (!sections.length) return null

  const section = sections[current]
  const hint = getHint(section.title)
  const progress = completed.size

  const markComplete = (idx: number) => setCompleted(prev => new Set(prev).add(idx))

  const goTo = (idx: number) => {
    setCurrent(idx)
    setSuggestion(null)
    setUserContext('')
  }

  const handleGenerate = async () => {
    if (!userContext.trim()) return
    setGenerating(true)
    try {
      const r = await generateSection({
        skill_name: skillName,
        section_title: section.title,
        section_content: section.content,
        user_context: userContext,
      })
      setSuggestion(r.data.generated_content)
    } catch {
      setToast('Failed to generate. Try again.')
      setTimeout(() => setToast(''), 3000)
    } finally {
      setGenerating(false)
    }
  }

  const applySuggestion = () => {
    if (!suggestion) return
    setSections(prev => prev.map((s, i) => i === current ? { ...s, content: suggestion } : s))
    setSuggestion(null)
    markComplete(current)
  }

  const handleNext = () => {
    markComplete(current)
    if (current < sections.length - 1) goTo(current + 1)
  }

  const handlePrev = () => {
    if (current > 0) goTo(current - 1)
  }

  const handleFinish = async () => {
    markComplete(current)
    setSaving(true)
    const body = sections.map(s => `## ${s.title}\n\n${s.content}`).join('\n\n')
    try {
      await updateSkill(skillName, { content: body })
      navigate(`/skills/${skillName}`, { replace: true })
    } catch {
      setToast('Failed to save. Try again.')
      setTimeout(() => setToast(''), 3000)
    } finally {
      setSaving(false)
    }
  }

  const isLast = current === sections.length - 1

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text1)', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <div style={{
        height: 52, display: 'flex', alignItems: 'center', padding: '0 20px', gap: 12,
        borderBottom: '1px solid var(--border)', background: 'var(--surface)', flexShrink: 0,
      }}>
        <button onClick={() => navigate('/templates')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, padding: 0 }}>
          <svg width="24" height="24" viewBox="0 0 28 28"><rect width="28" height="28" rx="6" fill="#7c3aed"/><rect x="4" y="7" width="20" height="4.5" rx="2.25" fill="white" transform="rotate(-10,14,9.25)"/><rect x="4" y="14" width="14" height="4.5" rx="2.25" fill="white" opacity=".55" transform="rotate(-10,11,16.25)"/><rect x="4" y="21" width="8" height="4.5" rx="2.25" fill="white" opacity=".22" transform="rotate(-10,8,23.25)"/></svg>
          <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--text1)' }}>
            Skil<span style={{ color: 'var(--brand-light)' }}>flow</span>
          </span>
        </button>
        <span style={{ color: 'var(--text3)', fontSize: 13 }}>/</span>
        <button onClick={() => navigate('/templates')} style={{ background: 'none', border: 'none', color: 'var(--text2)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Templates</button>
        <span style={{ color: 'var(--text3)', fontSize: 13 }}>/</span>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text1)' }}>{templateTitle}</span>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => navigate(`/skills/${skillName}`)}
          style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Skip guided setup — edit in full editor &rarr;
        </button>
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left sidebar */}
        <div style={{
          width: 220, flexShrink: 0, background: 'var(--surface)', borderRight: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column', padding: '16px 0',
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, padding: '0 16px', marginBottom: 12 }}>
            Sections
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {sections.map((s, i) => {
              const isDone = completed.has(i)
              const isCurrent = i === current
              return (
                <button key={i} onClick={() => goTo(i)} style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 16px', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  background: isCurrent ? 'var(--brand-dim)' : 'transparent',
                  borderLeft: isCurrent ? '2px solid var(--brand)' : '2px solid transparent',
                  textAlign: 'left',
                }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 700,
                    background: isDone ? '#10b981' : isCurrent ? 'var(--brand)' : 'var(--surface2)',
                    color: isDone || isCurrent ? '#fff' : 'var(--text3)',
                  }}>
                    {isDone ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    ) : i + 1}
                  </div>
                  <span style={{
                    fontSize: 12, fontWeight: isCurrent ? 600 : 400,
                    color: isCurrent ? 'var(--text1)' : isDone ? 'var(--text2)' : 'var(--text3)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {s.title}
                  </span>
                </button>
              )
            })}
          </div>
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
            <div style={{ height: 3, background: 'var(--surface2)', borderRadius: 2, overflow: 'hidden', marginBottom: 6 }}>
              <div style={{ height: '100%', width: `${(progress / sections.length) * 100}%`, background: 'linear-gradient(90deg, #7c3aed, #a78bfa)', borderRadius: 2, transition: 'width 0.3s' }} />
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{progress} of {sections.length} sections complete</div>
          </div>
        </div>

        {/* Center panel */}
        <div style={{ flex: 1, overflow: 'auto', padding: '32px 40px' }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{
                width: 26, height: 26, borderRadius: '50%', background: 'var(--brand)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, color: '#fff',
              }}>
                {current + 1}
              </span>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, fontFamily: 'Sora, sans-serif' }}>{section.title}</h2>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text2)', margin: 0 }}>{hint.subtitle}</p>
          </div>

          {/* AI explanation */}
          <div style={{
            background: 'var(--brand-dim)', border: '1px solid rgba(124,58,237,0.12)',
            borderLeft: '3px solid var(--brand)', borderRadius: '0 10px 10px 0',
            padding: '16px 18px', marginBottom: 24,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ width: 20, height: 20, borderRadius: 6, background: 'var(--brand)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="10" height="10" viewBox="0 0 28 28"><rect width="28" height="28" rx="6" fill="white"/></svg>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-light)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Skilflow AI</span>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, margin: 0 }}>{hint.aiExplanation}</p>
          </div>

          {/* Context prompt */}
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
            padding: '18px 20px', marginBottom: 24,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)', marginBottom: 10 }}>
              {hint.contextPrompt}
            </div>
            <textarea
              value={userContext}
              onChange={e => setUserContext(e.target.value)}
              placeholder="Describe your team, use case, or preferences..."
              style={{
                width: '100%', minHeight: 80, padding: '10px 12px', borderRadius: 8,
                background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text1)',
                fontSize: 13, fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
              <button
                onClick={handleGenerate}
                disabled={generating || !userContext.trim()}
                className="btn-primary"
                style={{ padding: '9px 18px', fontSize: 13, opacity: generating || !userContext.trim() ? 0.5 : 1 }}
              >
                {generating ? 'Generating...' : 'Generate customized version'} &rarr;
              </button>
              <button
                onClick={() => markComplete(current)}
                style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Skip — edit manually
              </button>
            </div>
          </div>

          {/* Section content editor */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Section content</span>
              <button
                onClick={() => {
                  const original = (state?.sections || [])[current]
                  if (original) setSections(prev => prev.map((s, i) => i === current ? { ...s, content: original.content } : s))
                }}
                style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Restore default
              </button>
            </div>
            <textarea
              value={section.content}
              onChange={e => setSections(prev => prev.map((s, i) => i === current ? { ...s, content: e.target.value } : s))}
              style={{
                width: '100%', minHeight: 200, padding: '14px 16px', borderRadius: 8,
                background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text1)',
                fontSize: 13, fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                lineHeight: 1.6, resize: 'vertical', outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        {/* Right panel — AI suggestion */}
        {suggestion !== null && (
          <div style={{
            width: 320, flexShrink: 0, background: 'var(--surface)', borderLeft: '1px solid var(--border)',
            display: 'flex', flexDirection: 'column', padding: 20, overflow: 'auto',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <div style={{ width: 20, height: 20, borderRadius: 6, background: 'var(--brand)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="10" height="10" viewBox="0 0 28 28"><rect width="28" height="28" rx="6" fill="white"/></svg>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text1)' }}>AI suggestion</span>
              <button onClick={() => setSuggestion(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text3)', fontSize: 16, cursor: 'pointer' }}>&times;</button>
            </div>
            <textarea
              value={suggestion}
              onChange={e => setSuggestion(e.target.value)}
              style={{
                flex: 1, width: '100%', padding: '12px 14px', borderRadius: 8,
                background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text1)',
                fontSize: 12, fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                lineHeight: 1.6, resize: 'none', outline: 'none', boxSizing: 'border-box',
                minHeight: 200,
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
              <button onClick={applySuggestion} className="btn-primary" style={{ padding: '10px 16px', fontSize: 13, width: '100%' }}>
                Apply this version
              </button>
              <button onClick={() => setSuggestion(null)} className="btn-ghost" style={{ padding: '10px 16px', fontSize: 13, width: '100%' }}>
                Keep original
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom navigation */}
      <div style={{
        height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', borderTop: '1px solid var(--border)', background: 'var(--surface)', flexShrink: 0,
      }}>
        <button
          onClick={handlePrev}
          disabled={current === 0}
          className="btn-ghost"
          style={{ padding: '8px 18px', fontSize: 13, opacity: current === 0 ? 0.3 : 1, cursor: current === 0 ? 'default' : 'pointer' }}
        >
          &larr; Previous
        </button>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>
          Section {current + 1} of {sections.length}
        </div>
        {isLast ? (
          <button onClick={handleFinish} disabled={saving} className="btn-primary" style={{ padding: '9px 20px', fontSize: 13, opacity: saving ? 0.5 : 1 }}>
            {saving ? 'Saving...' : 'Finish & save skill'} &rarr;
          </button>
        ) : (
          <button onClick={handleNext} className="btn-primary" style={{ padding: '9px 20px', fontSize: 13 }}>
            Next section &rarr;
          </button>
        )}
      </div>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 500, zIndex: 400,
          background: 'var(--text1)', color: 'var(--bg)', whiteSpace: 'nowrap',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        }}>
          {toast}
        </div>
      )}
    </div>
  )
}
